import moment from 'moment';
import { images } from '../../images';
import { IBase, IHolidayCalendar, IHolidayCalendarData, IRouteSetupDetail } from '../models';
import { InputType } from '../models/enums';
import { LiteralService } from '../services/literal/literal.service';
import { formData } from './setupData';
import { HolidayCalendarsService } from '../services';
import { CheckboxComponent } from '../shared/input';

let holidayCalendarsPage = 0;
let holidayCalendarsSearchPage = 0;
let holidayCalendarsItems: any[] = [];
let holidayCalendarsSearched: any[] = [];

async function loadHolidayCalendars(holidayCalendarsService: HolidayCalendarsService, clientId: number, selectedRefs: Map<number | undefined, IBase>, page: number, search?: string) {
  const result = await holidayCalendarsService.getHolidayCalendarsByClientId(clientId, search, page, 20);
  if (!Array.isArray(result)) {
    console.warn('Unexpected holiday calendars response (expected array):', result);
    return [];
  }
  return result
    .filter((calendar: IHolidayCalendar | null | undefined): calendar is IHolidayCalendar => !!calendar && calendar.id !== undefined && calendar.id !== null)
    .map((calendar: IHolidayCalendar) => {
      const existing = selectedRefs.get(calendar.id);
      return { ...calendar, value: existing ?? calendar, label: calendar.name };
    });
}

function mergeUniqueById(target: any[], incoming: any[]): any[] {
  const seen = new Set(target.map(i => i.id));
  for (const item of incoming) {
    if (!seen.has(item.id)) {
      target.push(item);
      seen.add(item.id);
    }
  }
  return target;
}

export async function getHolidayCalendarValues(holidayCalendarsService: HolidayCalendarsService, clientId: number, setupDetail: IRouteSetupDetail, holidayCalendarData?: IHolidayCalendarData): Promise<any[]> {
  const selectedRefs = new Map<number | undefined, IBase>((setupDetail?.holidayCalendars ?? []).map(s => [s.id, s]));

  if (holidayCalendarData) {
    if (holidayCalendarData.showMore) {
      holidayCalendarsPage += 1;
      holidayCalendarsSearchPage += 1;
    } else {
      holidayCalendarsItems = [];
      holidayCalendarsPage = 0;
      holidayCalendarsSearched = [];
      holidayCalendarsSearchPage = 0;
    }
    if (holidayCalendarData.search) {
      const results = await loadHolidayCalendars(holidayCalendarsService, clientId, selectedRefs, holidayCalendarsSearchPage, holidayCalendarData.value);
      mergeUniqueById(holidayCalendarsSearched, results);
    } else {
      const results = await loadHolidayCalendars(holidayCalendarsService, clientId, selectedRefs, holidayCalendarsPage);
      mergeUniqueById(holidayCalendarsItems, results);
    }
  } else {
    holidayCalendarsItems = [];
    holidayCalendarsPage = 0;
    holidayCalendarsSearched = [];
    holidayCalendarsSearchPage = 0;
    const results = await loadHolidayCalendars(holidayCalendarsService, clientId, selectedRefs, 0);
    mergeUniqueById(holidayCalendarsItems, results);
  }

  const activeCalendars = holidayCalendarData?.search ? holidayCalendarsSearched : holidayCalendarsItems;
  const selectedIds = new Set((setupDetail?.holidayCalendars ?? []).map((c: IBase) => c.id));

  const selectedFromList = (setupDetail?.holidayCalendars ?? []).map((selected: IBase) => {
    const fromList = activeCalendars.find((c: any) => c.id === selected.id);
    return fromList ?? { ...selected, value: selected, label: selected.name };
  });

  const rest = activeCalendars.filter((c: any) => !selectedIds.has(c.id));
  const combined = [...selectedFromList, ...rest];
  return Array.from(new Map(combined.map((c: any) => [c.id, c])).values());
}

export async function getHolidaysCalendar(setupDetail?: IRouteSetupDetail, holidayCalendarsService?: HolidayCalendarsService): Promise<any> {
  const data: any[] = [];
  if (!setupDetail?.client?.id || !holidayCalendarsService) return data;
  const res = await holidayCalendarsService.getHolidayCalendarsByClientId(setupDetail.client.id);
  res.forEach((holidayCalendar: any) => {
    data.push({ id: holidayCalendar.id, name: holidayCalendar.name, label: holidayCalendar.name });
  });
  return data;
}

export function getFunctionalities(setupDetail?: IRouteSetupDetail): unknown[] {
  const functionalities = [
    { id: "setup.data.showUsersPhone", value: setupDetail?.config.showUsersPhone, label: "showUsersPhone" },
    { id: "setup.data.tripReassignment", value: setupDetail?.config.tripReassignment, label: "tripReassignment" },
    { id: "setup.data.validateTasks", value: setupDetail?.config.validateTasks, label: "validateTasks" },
    { id: "setup.data.rejectTasks", value: setupDetail?.config.rejectTasks, label: "rejectTasks" },
    { id: "setup.data.rebuildNoShows", value: setupDetail?.config.rebuildNoShows, label: "rebuildNoShows" },
    { id: "setup.data.navigation", value: setupDetail?.config.navigation, label: "navigation" },
    { id: "setup.data.connections", value: setupDetail?.config.connections, label: "connections" },
  ];

  return setupDetail
    ? functionalities.filter(functionality => setupDetail?.config[functionality.label as keyof Object])
    : functionalities;
}

export async function getSetupGeneralForm(setupDetail: IRouteSetupDetail, newSetup: boolean, isEditing: boolean, literalService: LiteralService, holidayCalendarsService?: HolidayCalendarsService, holidayCalendarData?: IHolidayCalendarData): Promise<unknown[]> {

  let holidayCalendars: any[] = setupDetail?.holidayCalendars || [];
  if ((isEditing || newSetup) && setupDetail?.client?.id && holidayCalendarsService) {
    try {
      holidayCalendars = await getHolidayCalendarValues(holidayCalendarsService, setupDetail.client.id, setupDetail, holidayCalendarData);
    } catch (error) {
      console.error('Error loading holiday calendars:', error);
    }
  }

  let rangeDate: any[] = [];
  const isRangeFormatted = !newSetup && !isEditing;

  rangeDate = isRangeFormatted
    ? [
        moment(setupDetail?.fromDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        moment(setupDetail?.toDate, 'YYYY-MM-DD').format(' DD/MM/YYYY'),
      ]
    : [setupDetail?.fromDate, setupDetail?.toDate];

    let rangeData: any = rangeDate.toString().replace(',', ' - ');
    if (isEditing && !newSetup) {
      rangeData = rangeDate;
    } else if (newSetup) {
      rangeData = rangeDate;
    }

  const data: formData[] = [
    {
      img: images.setup.name,
      title: 'setup.data.name',
      value: setupDetail?.name || '',
      required: true,
      expanded: false,
      color: true,
      edit: {
        //placeholder: 'setup.data.name',
        // formControlName: 'name',
        inputType: InputType.TEXT,
        translateValue: false,
        required: true,
        valueToChange: 'name',
      },
    },
    {
      img: images.setup.time,
      title: 'setup.data.minutesBeforeReady',
      value: setupDetail?.config && setupDetail?.config.minutesBeforeReady,
      required: true,
      expanded: false,
      color: true,
      edit: {
        //placeholder: 'setup.data.minutesBeforeReady',
        // formControlName: 'minutesBeforeReady',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: true,
        valueToChange: 'minutesBeforeReady',
      },
    },
    {
      img: images.setup.since,
      title: 'setup.data.creationAdvancedDays',
      value: setupDetail?.config && setupDetail?.config.creationAdvancedDays || '',
      required: true,
      expanded: false,
      color: true,
      edit: {
        //placeholder: 'setup.data.creationAdvancedDays',
        // formControlName: 'creationAdvancedDays',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: true,
        valueToChange: 'creationAdvancedDays',
      },
    },
    {
      img: images.setup.since,
      title: 'setup.data.daysPeriod',
      value: rangeData,
      required: true,
      expanded: false,
      color: true,
      edit: {
        id: [ 'fromDate', 'toDate' ],
        formControlName: [ 'fromDate', 'toDate' ],
        inputType: InputType.RANGE_DATE,
        translateValue: false,
        disabled: [false, false],
        required: true,
        valueToChange: [ 'fromDate', 'toDate' ],
      },
    },
    {
      img: images.setup.calendar,
      title: 'setup.data.holidayCalendars',
      value: setupDetail?.holidayCalendars || [],
      array: true,
      required: false,
      expanded: false,
      color: true,
      edit: {
        id: setupDetail?.holidayCalendars,
        inputType: InputType.MULTISELECT_SCROLL,
        required: false,
        translateValue: false,
        values: holidayCalendars,
        selectedValues: setupDetail?.holidayCalendars,
        valueToChange: 'holidayCalendars',
        customSearchEnabled: true,
        allDays: true
      },
    },
    { title: ' ', hidden: true, edit: { inputType: InputType.TEXT, hidden: true} },
    { title: ' ', hidden: true, edit: { inputType: InputType.TEXT, hidden: true} },
    { title: ' ', hidden: true, edit: { inputType: InputType.TEXT, hidden: true} },
    {
      img: images.services.gtfs,
      title: 'setup.data.gtfsId',
      value: setupDetail?.config.gtfsId || '',
      required: false,
      expanded: false,
      color: true,
      edit: {
        placeholder: '-',
        // formControlName: 'name',
        inputType: InputType.TEXT,
        translateValue: false,
        required: true,
        valueToChange: 'gtfsId',
      },
    },
    {
      img: images.setup.duration,
      title: 'setup.data.maxReservationTimeWeb',
      value: setupDetail?.config.maxReservationTimeWeb,
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: false,
        valueToChange: 'maxReservationTimeWeb',
      }
    },
    {
      img: images.lastPage,
      title: 'setup.data.stickToTheEnd',
      value: isEditing ? setupDetail?.config.stickToTheEnd : (!newSetup ? (setupDetail?.config.stickToTheEnd ? literalService.get('activated', true) : literalService.get('deactivated', true)) : setupDetail?.config.stickToTheEnd),
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.TOGGLE,
        checkboxText: literalService.get('setup.show', true),
        translateValue: false,
        required: false,
        valueToChange: 'stickToTheEnd',
      }
    },
  ];
  return data;
}

export async function getSetupUserAppForm(setupDetail: IRouteSetupDetail, newSetup: boolean, isEditing: boolean, literalService: LiteralService): Promise<unknown[]> {
  // TODO: posar bé els formcontrolname i valueToChange (config.driverAppNextExpeditionsDaysLimit) i canviar en el changeData
  const data: formData[] = [
    {
      img: images.setup.dateTime,
      title: 'setup.data.mobileAppReservationLimit',
      value: isEditing ? setupDetail?.config.mobileAppReservationLimit : (!newSetup ? (setupDetail?.config.mobileAppReservationLimit ? literalService.get('activated', true) : literalService.get('deactivated', true)) : setupDetail?.config.mobileAppReservationLimit),
      required: false,
      expanded: false,
      color: true,
      edit: {
        id: 'mobileAppReservationLimit',
        formControlName: 'name',
        inputType: InputType.TOGGLE,
        translateValue: false,
        required: false,
        valueToChange: 'mobileAppReservationLimit',
      },
    },
    {
      img: images.setup.duration,
      title: 'setup.data.mobileAppReservationLimitMinutes',
      value: setupDetail?.config.mobileAppReservationLimit ? setupDetail?.config.mobileAppReservationLimitMinutes : undefined,
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        placeholder: '-',
        inputType: InputType.NUMBER,
        translateValue: false,
        disabled: !setupDetail?.config.mobileAppReservationLimit,
        required: false,
        valueToChange: 'mobileAppReservationLimitMinutes',
      },
    },
    {
      img: images.setup.time,
      title: 'setup.data.mobileAppReservationLimitPreviousDayHour',
      value: setupDetail?.config.mobileAppReservationLimit && setupDetail?.config.mobileAppReservationLimitPreviousDayHour ? moment(setupDetail?.config.mobileAppReservationLimitPreviousDayHour, 'HH:mm:ss').format('HH:mm') : '',
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        placeholder: '-',
        inputType: InputType.TIME,
        translateValue: false,
        disabled: !setupDetail?.config.mobileAppReservationLimit,
        required: false,
        valueToChange: 'mobileAppReservationLimitPreviousDayHour',
      },
    },
    {
      img: '',
      title: 'setup.data.mobileAppReservationLimitPreviousDayWorkingDay',
      value: !setupDetail?.config.mobileAppReservationLimit ? false :setupDetail?.config.mobileAppReservationLimitPreviousDayWorkingDay,
      component: CheckboxComponent,
      required: false,
      expanded: false,
      color: true,
      edit: {
        formControlName: 'name',
        inputType: InputType.CHECKBOX,
        checkboxText: literalService.get('setup.workingDay', true),
        translateValue: false,
        disabled: !setupDetail?.config.mobileAppReservationLimit,
        required: false,
        valueToChange: 'mobileAppReservationLimitPreviousDayWorkingDay',
      },
    },
    {
      img: images.setup.dateTime,
      title: 'setup.data.mobileAppCancelReservationLimit',
      value: isEditing ? setupDetail?.config.mobileAppCancelReservationLimit : (!newSetup ? (setupDetail?.config.mobileAppCancelReservationLimit ? literalService.get('activated', true) : literalService.get('deactivated', true)) : setupDetail?.config.mobileAppCancelReservationLimit),
      required: false,
      expanded: false,
      color: true,
      edit: {
        formControlName: 'name',
        inputType: InputType.TOGGLE,
        translateValue: false,
        required: false,
        valueToChange: 'mobileAppCancelReservationLimit',
      },
    },
    {
      img: images.setup.duration,
      title: 'setup.data.mobileAppCancelReservationLimitMinutes',
      value: setupDetail?.config.mobileAppCancelReservationLimit ? setupDetail?.config.mobileAppCancelReservationLimitMinutes : undefined,
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        placeholder: '-',
        inputType: InputType.NUMBER,
        translateValue: false,
        disabled: !setupDetail?.config.mobileAppCancelReservationLimit,
        required: false,
        valueToChange: 'mobileAppCancelReservationLimitMinutes',
      }
    },
    {
      img: images.setup.time,
      title: 'setup.data.mobileAppCancelReservationLimitPreviousDayHour',
      value: setupDetail?.config.mobileAppCancelReservationLimit && setupDetail?.config.mobileAppCancelReservationLimitPreviousDayHour ? moment(setupDetail?.config.mobileAppCancelReservationLimitPreviousDayHour, 'HH:mm:ss').format('HH:mm') : '',
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        placeholder: '-',
        inputType: InputType.TIME,
        translateValue: false,
        disabled: !setupDetail?.config.mobileAppCancelReservationLimit,
        required: false,
        valueToChange: 'mobileAppCancelReservationLimitPreviousDayHour',
      }
    },
    {
      img: '',
      title: 'setup.data.mobileAppCancelReservationLimitPreviousDayWorkingDay',
      value: !setupDetail?.config.mobileAppCancelReservationLimit ? false : setupDetail?.config.mobileAppCancelReservationLimitPreviousDayWorkingDay,
      component: CheckboxComponent,
      required: false,
      expanded: false,
      color: true,
      edit: {
        formControlName: 'name',
        inputType: InputType.CHECKBOX,
        checkboxText: literalService.get('setup.workingDay', true),
        translateValue: false,
        disabled: !setupDetail?.config.mobileAppCancelReservationLimit,
        required: false,
        valueToChange: 'mobileAppCancelReservationLimitPreviousDayWorkingDay',
      }
    },
    {
      img: images.setup.phoneContact,
      title: 'setup.data.showDriverContact',
      value: isEditing ? setupDetail?.config.showDriverContact : (!newSetup ? (setupDetail?.config.showDriverContact ? literalService.get('activated', true) : literalService.get('deactivated', true)) : setupDetail?.config.showDriverContact),
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.TOGGLE,
        checkboxText: literalService.get('setup.show', true),
        translateValue: false,
        required: false,
        valueToChange: 'showDriverContact',
      }
    },
    {
      img: images.setup.duration,
      title: 'setup.data.maxReservationTimeApp',
      value: setupDetail?.config.maxReservationTimeApp,
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: false,
        valueToChange: 'maxReservationTimeApp',
      }
    },
  ];
  return data;
}

export async function getSetupDriverAppForm(setupDetail: IRouteSetupDetail, newSetup: boolean, isEditing: boolean, literalService: LiteralService): Promise<unknown[]> {
  // TODO: posar bé els formcontrolname i valueToChange (config.driverAppNextExpeditionsDaysLimit) i canviar en el changeData
  const data: formData[] = [
    {
      img: images.setup.tag,
      title: 'setup.data.tag',
      value: setupDetail?.tag || '',
      required: false,
      expanded: false,
      color: true,
      edit: {
        placeholder: '-',
        // formControlName: 'tag',
        inputType: InputType.TEXT,
        translateValue: false,
        required: true,
        valueToChange: 'tag',
      },
    },
    {
      img: images.setup.since,
      title: 'setup.data.driverAppNextExpeditionsDaysLimit',
      value: setupDetail?.config.driverAppNextExpeditionsDaysLimit,
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: false,
        valueToChange: 'driverAppNextExpeditionsDaysLimit',
      },
    },
    {
      img: images.setup.since,
      title: 'setup.data.driverAppPastExpeditionsDaysLimit',
      value: setupDetail?.config.driverAppPastExpeditionsDaysLimit,
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: false,
        valueToChange: 'driverAppPastExpeditionsDaysLimit',
      },
    },
    {
      img: images.setup.time,
      title: 'setup.data.maxAdvancement',
      value: setupDetail?.config && setupDetail?.config.maxAdvancement || '',
      required: false,
      expanded: false,
      color: true,
      edit: {
        //placeholder: 'setup.data.updateFrequency',
        // formControlName: 'updateFrequency',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: true,
        valueToChange: 'maxAdvancement',
      },
    },
    {
      img: images.setup.frequency,
      title: 'setup.data.updateFrequency',
      value: setupDetail?.config && setupDetail?.config.updateFrequency || '',
      required: false,
      expanded: false,
      color: true,
      edit: {
        //placeholder: 'setup.data.updateFrequency',
        // formControlName: 'updateFrequency',
        inputType: InputType.NUMBER,
        translateValue: false,
        required: true,
        valueToChange: 'updateFrequency',
      },
    },
    {
      img: images.setup.functionalities,
      title: 'setup.data.functionalities',
      value: getFunctionalities(setupDetail),
      array: true,
      required: false,
      expanded: false,
      color: true,
      edit: {
        id:  getFunctionalities(setupDetail),
        inputType: InputType.MULTISELECT,
        translateValue: true,
        values: getFunctionalities(),
        selectedValues: getFunctionalities(setupDetail),
        valueToChange: [
          'phoneDrivers',
          'showUsersPhone',
          'tripReassignment',
          'validateTasks',
          'rejectTasks',
          'rebuildNoShows',
          'navigation',
          'backgroundTracking',
          'connections',
        ],
      },
    },
    { title: ' ', hidden: true, edit: { inputType: InputType.TEXT, hidden: true} },
    { title: ' ', hidden: true, edit: { inputType: InputType.TEXT, hidden: true} },
    {
      img: images.setup.phoneDrivers,
      title: 'setup.data.phoneDrivers',
      value: isEditing ? setupDetail?.config.phoneDrivers : (!newSetup ? (setupDetail?.config.phoneDrivers ? literalService.get('activated', true) : literalService.get('deactivated', true)) : setupDetail?.config.phoneDrivers),
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.TOGGLE,
        checkboxText: literalService.get('setup.show', true),
        translateValue: false,
        required: false,
        valueToChange: 'phoneDrivers',
      }
    },
    {
      img: images.setup.backgroundTracking,
      title: 'setup.data.backgroundTracking',
      value: isEditing ? setupDetail?.config.backgroundTracking : (!newSetup ? (setupDetail?.config.backgroundTracking ? literalService.get('activated', true) : literalService.get('deactivated', true)) : setupDetail?.config.backgroundTracking),
      required: false,
      expanded: false,
      color: true,
      edit: {
        // formControlName: 'name',
        inputType: InputType.TOGGLE,
        checkboxText: literalService.get('setup.show', true),
        translateValue: false,
        required: false,
        valueToChange: 'backgroundTracking',
      }
    },
    {
      img: images.setup.time,
      title: 'setup.data.minAutoCloseDelayOutsideWindow',
      value: setupDetail?.config.backgroundTracking ? setupDetail?.config.minAutoCloseDelayOutsideWindow : undefined,
      required: false,
      expanded: false,
      color: true,
      tooltipInfo: {
        text: 'setup.data.minAutoCloseDelayOutsideWindowInfo',
      },
      edit: {
        placeholder: '-',
        inputType: InputType.NUMBER,
        translateValue: false,
        disabled: !setupDetail?.config.backgroundTracking,
        required: false,
        valueToChange: 'minAutoCloseDelayOutsideWindow',
      }
    },
    {
      img: images.distance,
      title: 'setup.data.expeditionDetectionRadius',
      value: setupDetail?.config.backgroundTracking ? setupDetail?.config.expeditionDetectionRadius : undefined,
      required: false,
      expanded: false,
      color: true,
      tooltipInfo: {
        text: 'setup.data.expeditionDetectionRadiusInfo',
      },
      edit: {
        placeholder: '-',
        inputType: InputType.NUMBER,
        translateValue: false,
        disabled: !setupDetail?.config.backgroundTracking,
        required: false,
        valueToChange: 'expeditionDetectionRadius',
      }
    },
    {
      img: images.distance,
      title: 'setup.data.segmentDetectionRadius',
      value: setupDetail?.config.backgroundTracking ? setupDetail?.config.segmentDetectionRadius : undefined,
      required: false,
      expanded: false,
      color: true,
      tooltipInfo: {
        text: 'setup.data.segmentDetectionRadiusInfo',
      },
      edit: {
        placeholder: '-',
        inputType: InputType.NUMBER,
        translateValue: false,
        disabled: !setupDetail?.config.backgroundTracking,
        required: false,
        valueToChange: 'segmentDetectionRadius',
      }
    },
  ];
  return data;
}